import { useMediaQuery, Text, Box } from '@ltvco/refresh-lib/theme';
import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useContext,
} from 'react';

import {
  AnimatedButtonContainer,
  AnimatedInputButtonContainer,
  AnimatedInputContainer,
  AnimatedSendButton,
  ArrowUpwardIcon,
  AutosuggestionPrompts,
  ClickHereTooltip,
  DragDropOverlay,
  ExpandedPlaceholderContainer,
  ExpandedPlaceholderText,
  ImagePreview,
  ImageUploadButton,
  SendButton,
  StyledInputChat,
  StyledInputChatWithExpandedPlaceholder,
  TryWebSearchButton,
} from 'components/ChatBV/components';
import { PLACEHOLDER_COLLECTION } from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';
import { useChatForm } from 'components/ChatBV/hooks/useChatForm';
import { ChatMessageData } from 'components/ChatBV/types';
import {
  getTrailingSpaceForType,
  getUploadFileValidation,
  isPdfFile,
} from 'components/ChatBV/utils/generalUtils';

interface ChatInputProps {
  onSendMessage?: (data: ChatMessageData) => void;
  isReportOpen?: boolean;
  isLanding?: boolean;
  isAnimating?: boolean;
  animationPhase?: 'center' | 'moving-up' | 'moving-left' | 'header';
  onChipClick?: (chipTitle: string) => void;
  isLoading?: boolean;
}

export interface ChatInputRef {
  handleChipClick: (type: string) => void;
}

export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(
  (
    {
      onSendMessage,
      isReportOpen,
      isLanding = false,
      isAnimating = false,
      animationPhase = 'center',
      isLoading = false,
    },
    ref
  ) => {
    const {
      inputValue,
      handleInputChange,
      handleKeyPress,
      handleSendMessage,
      selectedImage,
      imagePreviewUrl,
      handleImageSelect,
      handleRemoveImage,
    } = useChatForm(onSendMessage);

    const context = useContext(ChatContext);
    const setImageUploadError = context?.setImageUploadError;

    const hasInputValue =
      inputValue.trim().length > 0 || selectedImage !== null;
    const isMobile = useMediaQuery('(max-width: 1024px)');
    const [isDragging, setIsDragging] = useState(false);
    const [termsPosition, setTermsPosition] = useState({
      top: 0,
      left: 0,
      width: 0,
    });
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [currentPlaceholder, setCurrentPlaceholder] = useState(
      'What can I help you find?'
    );
    const [expandedPlaceholder, setExpandedPlaceholder] = useState('');
    const [textWidth, setTextWidth] = useState(0);
    const [isFromClick, setIsFromClick] = useState(false);
    const inputRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const inputElementRef = useRef<HTMLTextAreaElement>(null);
    const savedCursorPositionRef = useRef<number | null>(null);

    //We will need to check if this is needed
    // Calculate text width for placeholder positioning
    useEffect(() => {
      if (inputValue) {
        // Use canvas for precise text measurement
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
          context.font =
            '16px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          const width = context.measureText(inputValue).width;
          setTextWidth(width);
        } else {
          const approximateWidth = inputValue.length * 8.5;
          setTextWidth(approximateWidth);
        }
      } else {
        setTextWidth(0);
      }
    }, [inputValue]);

    // Restore cursor position when component changes due to expandedPlaceholder
    useEffect(() => {
      if (savedCursorPositionRef.current !== null && inputElementRef.current) {
        requestAnimationFrame(() => {
          if (
            inputElementRef.current &&
            savedCursorPositionRef.current !== null
          ) {
            const newCursorPosition = Math.min(
              savedCursorPositionRef.current,
              inputValue.length
            );
            inputElementRef.current.setSelectionRange(
              newCursorPosition,
              newCursorPosition
            );
            savedCursorPositionRef.current = null;
          }
        });
      }
    }, [expandedPlaceholder, inputValue]);

    // Calculate the position to move the input to the chat page input position
    useEffect(() => {
      if (isLanding && isAnimating && animationPhase === 'moving-up') {
        const inputElement = inputRef.current;

        if (inputElement) {
          const inputRect = inputElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const chatPageInputPosition = isMobile
            ? viewportHeight - 80
            : viewportHeight - 120;
          const distanceToMove =
            chatPageInputPosition - (inputRect.top + inputRect.height / 2);

          setTermsPosition({
            top: distanceToMove,
            left: 0,
            width: 0,
          });
        }
      }
    }, [isLanding, isAnimating, animationPhase, isMobile]);

    const handleInputClick = () => {
      if (isLanding && !isAnimating) {
        setShowSuggestions(true);
      }
    };

    const handleInputChangeWithSuggestions = (
      event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      // Preserve cursor position and isFromClick state before state updates
      const textarea = event.target;
      const cursorPosition = textarea.selectionStart;
      const newValue = event.target.value;
      const wasFromClick = isFromClick;

      if (showSuggestions && !isFromClick) {
        setShowSuggestions(false);
      }

      if (expandedPlaceholder && !isFromClick) {
        setExpandedPlaceholder('');
      }

      if (isFromClick) {
        setIsFromClick(false);
      }

      if (currentPlaceholder !== 'What can I help you find?') {
        setCurrentPlaceholder('What can I help you find?');
      }

      // Auto-resize functionality
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;

      handleInputChange(event);

      // Save cursor position to restore after component re-render (only if not from programmatic click)
      if (!wasFromClick) {
        savedCursorPositionRef.current = cursorPosition;
        // Restore cursor position after state updates
        requestAnimationFrame(() => {
          if (
            inputElementRef.current &&
            savedCursorPositionRef.current !== null
          ) {
            // Calculate new cursor position (in case value changed programmatically)
            const newCursorPosition = Math.min(
              savedCursorPositionRef.current,
              newValue.length
            );
            inputElementRef.current.setSelectionRange(
              newCursorPosition,
              newCursorPosition
            );
            savedCursorPositionRef.current = null;
          }
        });
      }
    };

    const handleSelectPrompt = (
      suggestionMainText: string,
      suggestionExampleText: string,
      type: string
    ) => {
      // Calculate text width immediately for proper spacing
      if (inputValue.trim() !== suggestionMainText.trim()) {
        const approximateWidth = suggestionMainText.length * 8.5; // More accurate approximation
        setTextWidth(approximateWidth);

        // Different search types require different trailing spaces due to positioning calculation
        // PROPERTY needs 2 spaces, PERSON/PHONE/VEHICLE need 1 space, others need 0
        const trailingSpace = getTrailingSpaceForType(type, true);

        const syntheticEvent = {
          target: { value: `${suggestionMainText}${trailingSpace}` },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        setExpandedPlaceholder(suggestionExampleText);
        setIsFromClick(true);
        handleInputChange(syntheticEvent);

        // Set cursor position immediately after value change
        if (inputElementRef.current) {
          const textLength = `${suggestionMainText}${trailingSpace}`.length;
          inputElementRef.current.focus();
          // Use requestAnimationFrame to ensure the DOM has updated
          requestAnimationFrame(() => {
            if (inputElementRef.current) {
              inputElementRef.current.setSelectionRange(textLength, textLength);
              // Auto-resize after cursor positioning
              inputElementRef.current.style.height = 'auto';
              inputElementRef.current.style.height = `${Math.min(
                inputElementRef.current.scrollHeight,
                120
              )}px`;
            }
          });
        }
      }
    };

    const handleCloseSuggestions = () => {
      setShowSuggestions(false);
    };

    const handleChipClick = (type: string) => {
      // Only update if the value is different to prevent adding extra spaces
      const textData =
        PLACEHOLDER_COLLECTION.find(
          (item: { searchType: string }) => item.searchType === type
        ) || PLACEHOLDER_COLLECTION[0];
      if (inputValue.trim() !== textData.chipMainText.trim()) {
        // Calculate text width immediately for proper spacing
        // Different search types require different trailing spaces due to positioning calculation
        const trailingSpace = getTrailingSpaceForType(type, false);
        const approximateWidth = textData.chipMainText.length * 8.5;
        setTextWidth(approximateWidth);

        const syntheticEvent = {
          target: { value: `${textData.chipMainText}${trailingSpace}` },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        setExpandedPlaceholder(textData.chipExampleText);
        setIsFromClick(true);
        handleInputChange(syntheticEvent);
        if (inputElementRef.current) {
          const textLength = `${textData.chipMainText}${trailingSpace}`.length;
          inputElementRef.current.focus();
          // Use requestAnimationFrame to ensure the DOM has updated
          requestAnimationFrame(() => {
            if (inputElementRef.current) {
              inputElementRef.current.setSelectionRange(textLength, textLength);
              // Auto-resize after cursor positioning
              inputElementRef.current.style.height = 'auto';
              inputElementRef.current.style.height = `${Math.min(
                inputElementRef.current.scrollHeight,
                120
              )}px`;
            }
          });
        }
      }
    };

    useEffect(() => {
      const handleDragEnter = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.types.includes('Files')) {
          setIsDragging(true);
        }
      };

      const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.types.includes('Files')) {
          setIsDragging(true);
        }
      };

      const handleDragLeave = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const relatedTarget = e.relatedTarget as Node | null;
        if (
          (!relatedTarget || relatedTarget.nodeName === 'HTML') &&
          (e.clientX === 0 || e.clientY === 0)
        ) {
          setIsDragging(false);
        }
      };

      const handleDrop = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (isLoading) {
          return;
        }

        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
          const file = files[0];
          const { isValidFile, isValidSize } = getUploadFileValidation(file);

          if (isValidFile && isValidSize) {
            handleImageSelect(file);
          } else if (!isValidFile && setImageUploadError) {
            setImageUploadError(
              "We couldn't upload your file. Please upload an image or PDF file."
            );
          } else if (isValidFile && !isValidSize && setImageUploadError) {
            setImageUploadError(
              "We couldn't upload your file. Check that the file is under 32 MB and try again."
            );
          }
        }
      };

      document.addEventListener('dragenter', handleDragEnter);
      document.addEventListener('dragover', handleDragOver);
      document.addEventListener('dragleave', handleDragLeave);
      document.addEventListener('drop', handleDrop);

      return () => {
        document.removeEventListener('dragenter', handleDragEnter);
        document.removeEventListener('dragover', handleDragOver);
        document.removeEventListener('dragleave', handleDragLeave);
        document.removeEventListener('drop', handleDrop);
      };
    }, [handleImageSelect, isLoading]);

    useImperativeHandle(ref, () => ({
      handleChipClick,
    }));

    return (
      <>
        <DragDropOverlay isVisible={isDragging} />

        <AnimatedInputContainer
          data-role="chat-input"
          ref={inputRef}
          isLanding={isLanding}
          isAnimating={isAnimating}
          animationPhase={animationPhase}
          termsPosition={termsPosition}
        >
          {!isLanding && <TryWebSearchButton />}
          <AnimatedInputButtonContainer
            isLanding={isLanding}
            isAnimating={isAnimating}
            animationPhase={animationPhase}
            showSuggestions={showSuggestions}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              {imagePreviewUrl && (
                <ImagePreview
                  imageUrl={imagePreviewUrl}
                  onRemove={handleRemoveImage}
                  isPdf={isPdfFile(selectedImage)}
                />
              )}
              {expandedPlaceholder && inputValue.trim() !== '' ? (
                <ExpandedPlaceholderContainer
                  sx={{
                    width: '100%',
                    flex: isLanding ? 'none' : 1,
                    ...(isAnimating && {
                      flex: 1,
                      minWidth: 0,
                    }),
                  }}
                >
                  <StyledInputChatWithExpandedPlaceholder
                    ref={inputElementRef}
                    placeholder=""
                    value={inputValue}
                    autoFocus={isLanding ? true : false}
                    onChange={handleInputChangeWithSuggestions}
                    onKeyPress={handleKeyPress}
                    onClick={handleInputClick}
                    disabled={isLoading}
                    rows={1}
                  />
                  <ExpandedPlaceholderText textWidth={textWidth}>
                    {expandedPlaceholder}
                  </ExpandedPlaceholderText>
                  <span
                    ref={measureRef}
                    style={{
                      position: 'absolute',
                      visibility: 'hidden',
                      whiteSpace: 'nowrap',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      fontWeight: 'inherit',
                      letterSpacing: 'inherit',
                      top: '-9999px',
                      left: '-9999px',
                    }}
                  >
                    {inputValue}
                  </span>
                </ExpandedPlaceholderContainer>
              ) : (
                <StyledInputChat
                  ref={inputElementRef}
                  placeholder={currentPlaceholder}
                  value={inputValue}
                  autoFocus={isLanding ? true : false}
                  onChange={handleInputChangeWithSuggestions}
                  onKeyPress={handleKeyPress}
                  onClick={handleInputClick}
                  disabled={isLoading}
                  rows={1}
                  sx={{
                    width: '100%',
                    flex: isLanding ? 'none' : 1,
                    ...(isAnimating && {
                      flex: 1,
                      minWidth: 0,
                    }),
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                margin: 0,
                padding: 0,
                marginTop: 1,
              }}
            >
              <AnimatedButtonContainer
                isReportOpen={isReportOpen}
                isLanding={isLanding}
                isAnimating={isAnimating}
                sx={{
                  marginTop: imagePreviewUrl ? '-10px' : 0,
                  marginLeft: 0,
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 'auto',
                  width: 'auto',
                }}
              >
                <AnimatedSendButton isAnimating={isAnimating}>
                  <ImageUploadButton
                    onImageSelect={handleImageSelect}
                    onError={
                      setImageUploadError
                        ? (error: string) => setImageUploadError(error)
                        : undefined
                    }
                    disabled={isLoading}
                  />
                </AnimatedSendButton>
              </AnimatedButtonContainer>
              <AnimatedButtonContainer
                isReportOpen={isReportOpen}
                isLanding={isLanding}
                isAnimating={isAnimating}
                sx={{
                  marginTop: imagePreviewUrl ? '-10px' : 0,
                  marginRight: 0,
                  paddingLeft: isAnimating ? '4px' : 0,
                  paddingRight: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <AnimatedSendButton isAnimating={isAnimating}>
                  <SendButton
                    onClick={handleSendMessage}
                    hasInputValue={hasInputValue}
                  >
                    <ArrowUpwardIcon hasInputValue={hasInputValue} />
                  </SendButton>
                </AnimatedSendButton>
              </AnimatedButtonContainer>
            </Box>
          </AnimatedInputButtonContainer>
          {isLanding && (
            <AutosuggestionPrompts
              isVisible={showSuggestions}
              onSelectPrompt={handleSelectPrompt}
              onClose={handleCloseSuggestions}
            />
          )}
          {!isLanding && (
            <Text
              fontSize="13px"
              color="text.secondary"
              textAlign="center"
              mt={2}
            >
              This chat feature is powered by artificial intelligence.{' '}
              <ClickHereTooltip>Click here</ClickHereTooltip> to view our AI
              usage disclaimer
            </Text>
          )}
        </AnimatedInputContainer>
      </>
    );
  }
);
